<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";

const route = useRoute();
const router = useRouter();
const companyId = route.params.id;

const company = ref(null);
const contacts = ref([]);
const deals = ref([]);
const cargando = ref(true);

function formatMoneda(valor) {
  return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(valor || 0);
}

const volver = () => router.push("/crm/companies");
const verContacto = (id) => router.push(`/crm/contacts/${id}`);

onMounted(async () => {
  cargando.value = true;
  try {
    const [companyRes, contactsRes, dealsRes] = await Promise.all([
      api.get(`/crm/companies/${companyId}`),
      api.get("/crm/contacts", { params: { crmCompanyId: companyId, limit: 200 } }),
      api.get("/crm/deals", { params: { crmCompanyId: companyId } })
    ]);

    company.value = companyRes.data.company;
    contacts.value = contactsRes.data.contacts;
    deals.value = dealsRes.data.deals;

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la empresa",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0">
        <i class="bi bi-building me-2"></i>
        Ficha de empresa
      </h4>

      <argon-button color="secondary" @click="volver">
        <i class="bi bi-arrow-left me-1"></i>Volver a Empresas
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else-if="company" class="row">

      <div class="col-md-4">
        <div class="card">
          <div class="card-body">
            <h5>{{ company.name }}</h5>
            <p class="mb-1"><i class="bi bi-briefcase me-2"></i>{{ company.industry || "Sin industria" }}</p>
            <p class="mb-1"><i class="bi bi-geo-alt me-2"></i>{{ [company.city, company.country].filter(Boolean).join(", ") || "—" }}</p>
            <p class="mb-1"><i class="bi bi-globe me-2"></i>
              <a v-if="company.website" :href="company.website" target="_blank">{{ company.website }}</a>
              <span v-else>—</span>
            </p>
            <p class="mb-1"><i class="bi bi-card-text me-2"></i>NIT: {{ company.nit || "—" }}</p>
            <p class="mb-1"><i class="bi bi-people me-2"></i>{{ company.employee_count || "—" }} empleados</p>
            <p class="mb-3"><i class="bi bi-cash-coin me-2"></i>{{ company.revenue ? formatMoneda(company.revenue) : "—" }} ingresos</p>

            <div v-if="company.notes" class="border-top pt-2">
              <div class="small text-muted mb-1">Observaciones</div>
              <div class="small">{{ company.notes }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-8">
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-person-lines-fill me-1"></i>Contactos ({{ contacts.length }})</h6>
          </div>
          <div class="card-body p-0">
            <div v-if="contacts.length === 0" class="text-muted small p-3">
              Sin contactos vinculados a esta empresa.
            </div>
            <ul class="list-group list-group-flush" v-else>
              <li
                v-for="c in contacts"
                :key="c.id"
                class="list-group-item d-flex justify-content-between align-items-center"
                style="cursor: pointer"
                @click="verContacto(c.id)"
              >
                <span>{{ c.name }} {{ c.last_name || "" }}</span>
                <small class="text-muted">{{ c.phone || c.email || "—" }}</small>
              </li>
            </ul>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-kanban me-1"></i>Negocios ({{ deals.length }})</h6>
          </div>
          <div class="card-body p-0">
            <div v-if="deals.length === 0" class="text-muted small p-3">
              Sin negocios vinculados a esta empresa.
            </div>
            <ul class="list-group list-group-flush" v-else>
              <li v-for="d in deals" :key="d.id" class="list-group-item d-flex justify-content-between align-items-center">
                <span>
                  {{ d.title }}
                  <span class="badge bg-gradient-secondary ms-1">{{ d.stage_name }}</span>
                </span>
                <small class="text-success fw-bold">{{ formatMoneda(d.value) }}</small>
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>
